import React, { useState, useEffect, useRef, useCallback } from "react";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import api from "../../core/data/api";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { toast } from "react-toastify";
import moment from "moment";
import Loading from "../../core/common/loader/Loading";
import {
  FaTrash,
  FaShare,
  FaClock,
  FaCalendarAlt,
  FaImage,
  FaDownload,
  FaTimes,
} from "react-icons/fa";
import SelectClassType from "../Users/SelectClassType";
import SelectCenter from "../Center/selectCenter";
import {
  isUploadAbortedError,
  showGalleryUploadErrorPopup,
} from "../../utils/galleryUploadErrors";
import {
  MAX_IMAGE_FILES,
  MAX_IMAGE_BYTES_PER_FILE,
  MAX_IMAGE_TOTAL_BYTES,
  MAX_IMAGE_MB_PER_FILE,
  MAX_IMAGE_TOTAL_MB,
  ALLOWED_IMAGE_TYPES,
  resolveImageMimeType,
  uploadImagesDirectToS3,
} from "../../utils/presignedS3Upload";

/** Matches `SelectCenter` placeholder option value */
const CENTER_PLACEHOLDER = " ";

const IMAGE_NAME_EXT = /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif)$/i;

function isLikelyImageFile(f: File): boolean {
  const t = f.type || "";
  if (t.startsWith("image/")) return true;
  if (t === "application/octet-stream" && IMAGE_NAME_EXT.test(f.name)) return true;
  if (!t && IMAGE_NAME_EXT.test(f.name)) return true;
  return false;
}

function isHeicLikeFile(f: File | undefined): boolean {
  if (!f) return false;
  return /image\/hei[cf]/i.test(f.type || "") || /\.hei[cf]$/i.test(f.name);
}

const Images = () => {
  const [selectedId, setSelectedId] = useState<string>("");
  const [selectedCenter, setSelectedCenter] = useState<string>(CENTER_PLACEHOLDER);
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState<{ src: string }[]>([]);
  const [message, setMessage] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);
  const [notify, setImages] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [shareSubmitting, setShareSubmitting] = useState(false);
  const [shareFileInputKey, setShareFileInputKey] = useState(0);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const previewImagesRef = useRef<string[]>([]);
  const user = localStorage.getItem("user");
  const shareAbortRef = useRef<AbortController | null>(null);
  const shareFileInputRef = useRef<HTMLInputElement | null>(null);

  const cleanupShareModal = useCallback(() => {
    setPreviewImages((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
    previewImagesRef.current = [];
    setMessage("");
    setFiles([]);
    setSelectedClass("");
    setSelectedCenter(CENTER_PLACEHOLDER);
    setDate("");
    setShareFileInputKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const el = document.getElementById("sendImage");
    if (!el) return;
    const onHidden = () => {
      shareAbortRef.current?.abort();
      shareAbortRef.current = null;
      setShareSubmitting(false);
      cleanupShareModal();
    };
    el.addEventListener("hidden.bs.modal", onHidden);
    return () => el.removeEventListener("hidden.bs.modal", onHidden);
  }, [cleanupShareModal]);

  const handleClass = (item: string) => {
    setSelectedClass(item);
  };

  const getData = async () => {
    setLoading(true);
    try {
      const res = await api.post("/api/mssql-procedure/execute/get", {
        procedureName: "ImagesGetAll",
        parameters: [],
      });
      setImages(res.data.centers);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const totalBytes = (list: File[]) => list.reduce((sum, f) => sum + (f?.size || 0), 0);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    let filesArray = Array.from(e.target.files);

    filesArray = filesArray.filter(isLikelyImageFile);
    const beforeType = filesArray.length;
    filesArray = filesArray.filter((f) =>
      ALLOWED_IMAGE_TYPES.has(resolveImageMimeType(f))
    );
    if (beforeType > 0 && filesArray.length === 0) {
      toast.error("Only JPEG, PNG, or WebP images are supported.");
      e.target.value = "";
      return;
    }

    if (filesArray.length > MAX_IMAGE_FILES) {
      toast.error(`You can upload maximum ${MAX_IMAGE_FILES} images at a time.`);
      filesArray = filesArray.slice(0, MAX_IMAGE_FILES);
    }

    const bytes = totalBytes(filesArray);
    if (bytes > MAX_IMAGE_TOTAL_BYTES) {
      toast.error(
        `Selected images exceed the total size limit. Please select fewer or smaller images.`
      );
      e.target.value = "";
      return;
    }
    for (const f of filesArray) {
      if (f.size > MAX_IMAGE_BYTES_PER_FILE) {
        toast.error(`Each image must be at most ${MAX_IMAGE_MB_PER_FILE} MB (${f.name}).`);
        e.target.value = "";
        return;
      }
    }

    setPreviewImages((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      const next = filesArray.map((file) => URL.createObjectURL(file));
      previewImagesRef.current = next;
      return next;
    });
    setFiles(filesArray);
    e.target.value = "";
  };

  const removeSelectedImageAt = (index: number) => {
    setPreviewImages((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      const next = prev.filter((_, i) => i !== index);
      previewImagesRef.current = next;
      return next;
    });
    setFiles((prev) => prev.filter((_, i) => i !== index));
    if (shareFileInputRef.current) {
      shareFileInputRef.current.value = "";
    }
  };

  const handleImage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedClass) {
      toast.error("Please select a class");
      return;
    }
    if (!message) {
      toast.error("Please enter the description");
      return;
    }
    if (!date) {
      toast.error("Please select the event date");
      return;
    }
    if (files.length === 0) {
      toast.error("Please select at least one image");
      return;
    }
    if (files.length > MAX_IMAGE_FILES) {
      toast.error(`You can upload maximum ${MAX_IMAGE_FILES} images at a time.`);
      return;
    }
    if (totalBytes(files) > MAX_IMAGE_TOTAL_BYTES) {
      toast.error(
        `Selected images exceed ${MAX_IMAGE_TOTAL_MB} MB total. Please select fewer or smaller images.`
      );
      return;
    }

    shareAbortRef.current?.abort();
    const controller = new AbortController();
    shareAbortRef.current = controller;
    setShareSubmitting(true);
    try {
      let imageBuffer: string[] = [];

      if (files.length > 0) {
        const { objectUrls } = await uploadImagesDirectToS3(files, {
          signal: controller.signal,
        });
        imageBuffer = objectUrls;
      }

      const images = imageBuffer.join(", ");

      const userId = user ? parseInt(user) : 0;

      const response = await api.post(
        "/api/mssql-procedure/execute",
        {
          procedureName: "ImageInsert",
          parameters: [
            { name: "Image", type: "VarChar", value: images },
            { name: "Message", type: "VarChar", value: message },
            { name: "ClassID", type: "Int", value: selectedClass },
            { name: "CenterID", type: "Int", value: selectedCenter },
            { name: "ACTIVE", type: "Int", value: 1 },
            { name: "EventDate", type: "VarChar", value: date },
            { name: "CREATED_BY", type: "Int", value: userId },
          ],
        },
        { signal: controller.signal }
      );

      if (response.data.success) {
        shareAbortRef.current = null;
        toast.success("Images shared successfully");
        getData();
        document.getElementById("closeModal")?.click();
      }
    } catch (error) {
      console.log("Error sending Image:", error);
      if (isUploadAbortedError(error)) {
        toast.info("Upload cancelled");
      } else {
        await showGalleryUploadErrorPopup(error);
      }
    } finally {
      setShareSubmitting(false);
      shareAbortRef.current = null;
    }
  };

  const handleDelete = async () => {
    
    setLoading(true);
    try {
      const res = await api.post("/api/mssql-procedure/execute", {
        procedureName: "ImageDelete",
        parameters: [
          { name: "ID", type: "Int", value: selectedId },
          { name: "CREATED_BY", type: "Int", value: user },
        ],
      });

      if (res.data) {
        toast.success("Image deleted successfully");
        getData();
      }
    } catch (error) {
      toast.error("Failed to delete image");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  // Revoke any remaining object URLs when leaving the page
  useEffect(() => {
    return () => {
      previewImagesRef.current.forEach((image) => URL.revokeObjectURL(image));
    };
  }, []);

  const chunkArray = (arr: string[], size: number) => {
    const chunked: string[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunked.push(arr.slice(i, i + size));
    }
    return chunked;
  };
  const openImageUrlFallback = (url: string) => {
    const href = /^https?:\/\//i.test(url)
      ? url
      : new URL(url, window.location.origin).href;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const downloadImage = async (
    url: string,
    options?: { notifyOnFallback?: boolean }
  ) => {
    try {
      const absolute = /^https?:\/\//i.test(url)
        ? url
        : new URL(url, window.location.origin).href;
      const parsedUrl = new URL(absolute);
      const key = decodeURIComponent(parsedUrl.pathname.replace(/^\//, ""));

      const res = await api.get(`/download?key=${encodeURIComponent(key)}`, {
        responseType: "blob",
      });

      const blob = res.data as Blob;
      if (!blob?.size) throw new Error("Empty download");

      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = key.split("/").pop() || "image.jpg";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Download failed", error);
      if (options?.notifyOnFallback) {
        toast.info("Opening image in a new tab…");
      }
      openImageUrlFallback(url);
    }
  };

  const downloadAllImages = (urls: string[]) => {
    if (urls.length === 0) {
      toast.info("No images to download");
      return;
    }
    urls.forEach((url) => downloadImage(url));
    toast.success(
      urls.length === 1
        ? "Download started"
        : `Download started for ${urls.length} images`
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="card">
          <div className="card-header pb-1">
            <div className="d-flex align-items-center justify-content-between flex-wrap">
              <div className="mb-3">
                <h4 className="text-primary">
                  <FaImage className="me-2" />
                  ImageGallery
                </h4>
                <p className="text-muted mb-0">Share and view images with your class</p>
              </div>
              <div className="d-flex align-items-center mb-3">
                <button
                  className="btn btn-primary me-2 d-flex align-items-center"
                  data-bs-target="#sendImage"
                  data-bs-toggle="modal"
                >
                  <FaShare className="me-2 text-white" />
                  Share Images
                </button>
              </div>
            </div>
          </div>

          <div className="card-body">
            {loading && notify.length === 0 ? (
              <div className="d-flex justify-content-center py-5">
                <Loading />
              </div>
            ) : notify && notify.length > 0 ? (
              <div className="row">
                {notify.map((e: any, i: number) => {
                  let imageStr = e?.Image || "";
                  let images: string[] = [];

                  if (imageStr.includes("<img")) {
                    const srcMatch = imageStr.match(/src="([^"]+)"/);
                    if (srcMatch && srcMatch[1]) {
                      images = srcMatch[1].split(",").map((url: any) => url.trim());
                    }
                  } else if (typeof imageStr === "string") {
                    images = imageStr.split(",").map((url) => url.trim());
                  }

                  if (images.length === 0) {
                    images = ["assets/images/peo (6).jpg"];
                  }

                  const imageChunks = chunkArray(images, 4);

                  return (
                    <div key={i} className="col-12 mb-4">
                      <div className="card shadow-sm">
                        <div className="card-header bg-light d-flex justify-content-between align-items-center">
                          <div>
                            <h5>Class : {e?.ClassName || 'UnKnown Class'}</h5>
                            <p className="mb-1">
                              <FaCalendarAlt className="me-1" />
                              {moment(e?.EventDate).format("LL")}
                            </p>
                            <p>{e?.Message}</p>
                          </div>
                          <div>
                            <button
                               data-bs-toggle="modal"
                        data-bs-target="#delete-modal"
                        onClick={() => setSelectedId(e.ID)}
                              className="btn btn-sm btn-danger me-2"
                            >
                              <FaTrash />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => downloadAllImages(images)}
                            >
                              <FaDownload className="me-1" />
                              Download All
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          {imageChunks.map((chunk, idx) => (
                            <div
                              className="row mb-3"
                              key={idx}
                            >
                              {chunk.map((img, index) => (
                                <div
                                  className="col-md-3 mb-2 position-relative"
                                  key={index}
                                >
                                  <img
                                    src={img}
                                    alt={`img-${index}`}
                                    className="img-fluid rounded"
                                    style={{ cursor: "pointer", maxHeight: 150, objectFit: "cover" }}
                                    onClick={() => {
                                      setLightboxImages(images.map((src) => ({ src })));
                                      setCurrentIndex(i * 4 + idx * 4 + index);
                                      setLightboxOpen(true);
                                    }}
                                  />
                                  <button
                                    className="btn btn-sm btn-dark position-absolute top-0 end-0 m-1"
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      downloadImage(img, { notifyOnFallback: true });
                                    }}
                                  >
                                    <FaDownload />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>No images found</div>
            )}
          </div>
        </div>

        {/* Lightbox */}
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={lightboxImages}
          index={currentIndex}
        />
        
        <div className="modal fade" id="delete-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center">
                <span className="delete-icon">
                  <i className="ti ti-trash-x" />
                </span>
                <h4>Confirm Deletion</h4>
                <p>This action cannot be undone.</p>
                <div className="d-flex justify-content-center gap-3">
                  <button className="btn btn-light" data-bs-dismiss="modal">
                    Cancel
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleDelete}
                    data-bs-dismiss="modal"
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Modal for sharing images */}
        <div
          className="modal fade"
          id="sendImage"
          tabIndex={-1}
          aria-hidden="true"
          data-bs-backdrop="static"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title text-white">
                  <FaShare className="me-2 text-white" />
                  Share Images
                </h5>
                <button
                  type="button"
                  className="btn-close text-white"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  id="closeModal"
                ></button>
              </div>
              <form onSubmit={handleImage}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Select Center</label>
                    <SelectCenter
                      selectedCenter={selectedCenter}
                      handleSelect={(val: any) => setSelectedCenter(val)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Select Class</label>
                    <SelectClassType
                      type=""
                      selectedClass={selectedClass}
                      selectedCenter={selectedCenter}
                      handleSelect={(val: any) => setSelectedClass(val)}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="dateInput" className="form-label">
                      Event Date
                    </label>
                    <input
                      type="date"
                      id="dateInput"
                      className="form-control"
                      value={date}
                      min={new Date().toISOString().split("T")[0]}             
                      onChange={(e) => setDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="messageInput" className="form-label">
                      Description
                    </label>
                    <textarea
                      id="messageInput"
                      className="form-control"
                      rows={3}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                    ></textarea>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="fileInput" className="form-label">
                      Select Images
                    </label>
                    <input
                      key={shareFileInputKey}
                      ref={shareFileInputRef}
                      type="file"
                      id="fileInput"
                      className="form-control"
                      onChange={handleFile}
                      multiple
                      accept="image/*"
                    />
                    {files.length > 0 && (
                      <small className="text-muted d-block mt-1">
                        {files.length} image{files.length !== 1 ? "s" : ""} selected
                      </small>
                    )}
                  </div>
                  {previewImages.length > 0 && (
                    <div className="row g-2">
                      {previewImages.map((src, idx) => (
                        <div className="col-6 col-md-3 mb-2" key={`${src}-${idx}`}>
                          <div className="position-relative d-inline-block w-100">
                            {isHeicLikeFile(files[idx]) ? (
                              <div
                                className="rounded w-100 bg-light border d-flex align-items-center justify-content-center text-muted small px-2 text-center"
                                style={{ minHeight: 120, maxHeight: 120 }}
                              >
                                HEIC / HEIF — preview not supported in browser; file will still upload.
                              </div>
                            ) : (
                              <img
                                src={src}
                                alt={files[idx]?.name || `preview-${idx}`}
                                className="img-fluid rounded w-100"
                                style={{ maxHeight: 120, objectFit: "cover", display: "block" }}
                              />
                            )}
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle p-0 d-flex align-items-center justify-content-center"
                              style={{ width: 28, height: 28 }}
                              title="Remove image"
                              aria-label="Remove image"
                              onClick={() => removeSelectedImageAt(idx)}
                              disabled={shareSubmitting}
                            >
                              <FaTimes size={12} />
                            </button>
                          </div>
                          {files[idx]?.name && (
                            <small
                              className="text-muted text-truncate d-block mt-1"
                              title={files[idx].name}
                            >
                              {files[idx].name}
                            </small>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={shareSubmitting}
                  >
                    {shareSubmitting ? "Sharing..." : "Share"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Images;